import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { KibButtonNew } from '@chewy/kib-controls-react'
import { KibTopLevelAlert } from '@chewy/kib-messaging-react'
import { LanguageUtils } from '@pbt/pbt-ui-components'

import type {
  InventoryOrder,
  InventoryOrderItem,
} from '~/api/graphql/generated/types'
import DialogNames from '~/constants/DialogNames'
import {
  createInventoryOrderItemReceipts,
  getInventoryOrderError,
  getInventoryOrdersIsUpdating,
} from '~/store/duck/inventoryOrders'
import { getPackageType, getProductSizeUnit } from '~/store/reducers/constants'
import useCloseAfterCreation from '~/utils/useCloseAfterCreation'
import useDialog from '~/utils/useDialog'

import {
  getOneUomPluralized,
  getPluralPackageLabel,
} from '../../inventoryUtils'
import { isReceivingDisabled } from '../utils'
import { useConfirmReceiveOverage } from './hooks/useConfirmReceiveOverage'
import { useDraftReceipts } from './hooks/useDraftReceipts'
import {
  type ReceiptFieldGroup,
  useReceiptFields,
} from './hooks/useReceiptFields'
import { OrderItemReceiptsList } from './OrderItemReceiptsList'
import { OrderItemReceiptsPageHeader } from './OrderItemReceiptsPageHeader'
import { OrderItemReceiptsSummary } from './OrderItemReceiptsSummary'

import '@chewy/kib-messaging-styles/dist/styles/kib-alert-icon.css'
import '@chewy/kib-messaging-styles/dist/styles/kib-top-level-alert.css'
import styles from './OrderItemReceiptsPage.module.scss'

interface ValidationError {
  message: string
  reason?: string
}

const getTotalReceiptQuantities = (receipts: ReceiptFieldGroup[]) => {
  const { totalQuantityDamaged, totalQuantityReceived } = receipts.reduce(
    (totals, { fields }) => {
      const quantity = fields.receivedQuantity.value || 0
      if (fields.damaged.value) {
        totals.totalQuantityDamaged += quantity
      } else {
        totals.totalQuantityReceived += quantity
      }
      return totals
    },
    { totalQuantityDamaged: 0, totalQuantityReceived: 0 },
  )

  return { totalQuantityDamaged, totalQuantityReceived }
}

interface OrderItemReceiptsPageProps {
  inventoryOrder: InventoryOrder
  inventoryOrderItem: InventoryOrderItem
}

export const OrderItemReceiptsPage = ({
  inventoryOrder,
  inventoryOrderItem,
}: OrderItemReceiptsPageProps) => {
  const { t } = useTranslation(['Common', 'Admin'])
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const isUpdating = useSelector(getInventoryOrdersIsUpdating)
  const error = useSelector(getInventoryOrderError)
  const { receiptFields } = useReceiptFields(inventoryOrderItem.receipts)
  const {
    draftReceiptFields,
    addDraftReceipt,
    deleteDraftReceipt,
    validateDraftReceipts,
    getDraftReceiptsForSubmission,
  } = useDraftReceipts()
  const confirmReceiveOverage = useConfirmReceiveOverage(inventoryOrderItem)
  const ProductSizeUnit = useSelector(getProductSizeUnit)
  const PackageType = useSelector(getPackageType)
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>(
    [],
  )

  const [openConfirmCloseDialog, closeConfirmCloseDialog] = useDialog(
    DialogNames.CONFIRM_CLOSE_DIALOG,
  )

  const { totalQuantityDamaged, totalQuantityReceived } =
    getTotalReceiptQuantities([...receiptFields, ...draftReceiptFields])

  const {
    totalQuantityDamaged: draftQuantityDamaged,
    totalQuantityReceived: draftQuantityReceived,
  } = getTotalReceiptQuantities(draftReceiptFields)

  const isViewOnly = isReceivingDisabled(
    inventoryOrder.status,
    inventoryOrderItem.status,
  )

  const navigateToOrderItemsPage = () => {
    navigate(`/admin/catalog/inventories/orders/${inventoryOrder.id}`)
  }

  const closeAfterSaving = useCloseAfterCreation(() => {
    if (!error) {
      navigateToOrderItemsPage()
    }
  }, getInventoryOrdersIsUpdating)

  const submitReceipts = () => {
    const receiptsToSave = getDraftReceiptsForSubmission()
    closeAfterSaving()
    dispatch(
      createInventoryOrderItemReceipts(
        inventoryOrder.id,
        inventoryOrderItem.id,
        receiptsToSave,
      ),
    )
  }

  const getTotalQuantity = () => totalQuantityReceived + totalQuantityDamaged

  const getDraftQuantity = () => draftQuantityReceived + draftQuantityDamaged

  const getPlural = (unitName: string, amount: number) =>
    getOneUomPluralized(unitName, amount)

  const hasIncompleteFields = () =>
    draftReceiptFields.some(({ fields }) =>
      Object.values(fields).some((field) => !field.valid),
    )

  const hasExceededOrderedQuantity = () =>
    getTotalQuantity() > inventoryOrderItem.orderedQuantity

  const hasInvalidUomQuantity = () => {
    const packageSize = inventoryOrderItem.perPackageAmount
    return getDraftQuantity() % packageSize !== 0
  }

  const isOnlyOverageError = (errors: ValidationError[]): boolean => {
    if (errors.length !== 1) {
      return false
    }
    return (
      errors[0].message ===
      t('Admin:CATALOG.ORDERS.RECEIVING_QTY_EXCEEDS_ORDERED')
    )
  }

  const buildValidationErrors = (): ValidationError[] => {
    const errors: ValidationError[] = []

    const packageType = getPluralPackageLabel(
      inventoryOrderItem.packageType.id,
      PackageType,
    )
    const productSizeUnit = LanguageUtils.getConstantTranslatedName(
      inventoryOrderItem.packageUnits.id,
      ProductSizeUnit,
    )

    const packageSize = inventoryOrderItem.perPackageAmount
    const productSizeUnitForPackage = getPlural(productSizeUnit, packageSize)
    const packageSizeWithUnit = `${packageSize} ${productSizeUnitForPackage}`

    if (hasIncompleteFields()) {
      errors.push({
        message: t('Admin:CATALOG.ORDERS.RECEIVING_INCOMPLETE_FIELDS'),
      })
    }

    if (hasExceededOrderedQuantity()) {
      errors.push({
        message: t('Admin:CATALOG.ORDERS.RECEIVING_QTY_EXCEEDS_ORDERED'),
        reason: t('Admin:CATALOG.ORDERS.RECEIVING_QTY_EXCEEDS_ORDERED_REASON'),
      })
    }

    if (hasInvalidUomQuantity()) {
      const totalQuantity = getDraftQuantity()
      const quantityRatio = totalQuantity / packageSize
      const packageTypeRatio = getPlural(packageType, quantityRatio)
      const packageTypeSingular = getPlural(packageType, 1)
      const productSizeUnitTotal = getPlural(productSizeUnit, totalQuantity)

      errors.push({
        message: t('Admin:CATALOG.ORDERS.RECEIVING_INVALID_UOM_QTY', {
          packageSizeWithUnit,
          packageTypeSingular,
        }),
        reason: t('Admin:CATALOG.ORDERS.RECEIVING_INVALID_UOM_QTY_REASON', {
          totalQuantity,
          productSizeUnitTotal,
          quantityRatio,
          packageSizeWithUnit,
          packageTypeRatio,
          packageTypeSingular,
        }),
      })
    }

    return errors
  }

  const handleSaveDraftReceipts = () => {
    if (!draftReceiptFields.length) {
      return
    }

    const errors = buildValidationErrors()
    const hasValidationErrors = errors.length > 0
    const hasInvalidDraftReceipts = !validateDraftReceipts()

    if (hasValidationErrors || hasInvalidDraftReceipts) {
      setValidationErrors(errors)

      if (isOnlyOverageError(errors) && !hasInvalidDraftReceipts) {
        confirmReceiveOverage({
          onConfirm: (proceed: boolean) => {
            if (proceed) {
              submitReceipts()
            }
          },
          totalQuantityOrdered: inventoryOrderItem.orderedQuantity,
          totalQuantityReceived: getTotalQuantity(),
        })
        return
      }

      return
    }

    setValidationErrors([])

    submitReceipts()
  }

  const handleCancel = () => {
    if (draftReceiptFields.length > 0) {
      openConfirmCloseDialog({
        // Save
        onOk: () => {
          handleSaveDraftReceipts()
          closeConfirmCloseDialog()
        },
        // Don't Save
        onNotOk: () => {
          navigateToOrderItemsPage()
          closeConfirmCloseDialog()
        },
      })
      return
    }
    navigateToOrderItemsPage()
  }

  useEffect(() => {
    const shouldCreateInitialDraftReceipt =
      !isViewOnly && !(receiptFields.length || draftReceiptFields.length)
    if (shouldCreateInitialDraftReceipt) {
      addDraftReceipt()
    }
  }, [isViewOnly, receiptFields, draftReceiptFields])

  const isSaveDisabled = isUpdating || !draftReceiptFields.length || isViewOnly

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <OrderItemReceiptsPageHeader
          inventoryOrder={inventoryOrder}
          inventoryOrderItem={inventoryOrderItem}
        />
        {validationErrors.length > 0 &&
          validationErrors.map((entry) => (
            <KibTopLevelAlert
              className={styles.errorAlert}
              heading={entry.message}
              key={entry.message}
              severity="error"
            >
              {entry.reason}
            </KibTopLevelAlert>
          ))}
        <div className={styles.receiptsList}>
          <OrderItemReceiptsList
            addDraftReceipt={addDraftReceipt}
            deleteDraftReceipt={deleteDraftReceipt}
            draftReceiptFields={draftReceiptFields}
            inventoryOrderItem={inventoryOrderItem}
            isViewOnly={isViewOnly}
            receiptFields={receiptFields}
          />
        </div>
        {isViewOnly && (
          <div className={styles.backButton}>
            <KibButtonNew onClick={navigateToOrderItemsPage}>
              {t('Common:GO_BACK')}
            </KibButtonNew>
          </div>
        )}
      </div>
      {!isViewOnly && (
        <div className={styles.footer}>
          <div className={styles.buttons}>
            <KibButtonNew
              disabled={isSaveDisabled}
              loading={isUpdating}
              onClick={handleSaveDraftReceipts}
            >
              {t('Common:SAVE_ACTION')}
            </KibButtonNew>
            <KibButtonNew emphasis="secondary" onClick={handleCancel}>
              {t('Common:CANCEL_ACTION')}
            </KibButtonNew>
          </div>
          <OrderItemReceiptsSummary
            inventoryOrderItem={inventoryOrderItem}
            orderedQuantity={inventoryOrderItem.orderedQuantity}
            totalQuantityDamaged={totalQuantityDamaged}
            totalQuantityReceived={totalQuantityReceived}
          />
        </div>
      )}
    </div>
  )
}
